import { useState, useEffect, useCallback } from 'react';
import api, { API_BASE } from '../api';
import { T } from '../styles/theme';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const COLORS = { violet: '#a78bfa', green: '#4ade80', amber: '#fbbf24', cyan: '#22d3ee', red: '#f87171' };

function KpiCard({ icon, label, value, sub, color = T.violet }) {
  return (
    <div style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 14, padding: '18px 20px', flex: 1, minWidth: 150 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: '1.1rem' }}>{icon}</span>
        <span style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color, fontFamily: 'Syne,sans-serif' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ChurnBadge({ rischio }) {
  const map = { basso: { bg: 'rgba(74,222,128,0.12)', border: '#4ade80', color: '#4ade80', label: '🟢 Basso' }, medio: { bg: 'rgba(251,191,36,0.12)', border: '#fbbf24', color: '#fbbf24', label: '🟡 Medio' }, alto: { bg: 'rgba(248,113,113,0.12)', border: '#f87171', color: '#f87171', label: '🔴 Alto' } };
  const s = map[rischio] || map.basso;
  return <span style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color, borderRadius: 9999, padding: '2px 8px', fontSize: '0.65rem', fontWeight: 700 }}>{s.label}</span>;
}

function PianoBadge({ piano }) {
  return piano === 'pro'
    ? <span style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid #a78bfa', color: '#a78bfa', borderRadius: 9999, padding: '2px 8px', fontSize: '0.65rem', fontWeight: 700 }}>PRO</span>
    : <span style={{ background: 'rgba(75,85,99,0.3)', border: '1px solid #4b5563', color: '#9ca3af', borderRadius: 9999, padding: '2px 8px', fontSize: '0.65rem', fontWeight: 700 }}>FREE</span>;
}

function AccessoBadge({ data }) {
  if (!data) return <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</span>;
  const days = Math.floor((Date.now() - new Date(data).getTime()) / 86400000);
  const color = days === 0 ? '#4ade80' : days <= 7 ? '#fbbf24' : '#f87171';
  return <span style={{ color, fontSize: '0.75rem', fontWeight: 600 }}>{data}</span>;
}

function ProgressBar({ value, max = 100, color = '#7c3aed' }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div style={{ background: '#2d1b69', borderRadius: 9999, height: 6, width: '100%', overflow: 'hidden' }}>
      <div style={{ background: `linear-gradient(90deg, #7c3aed, ${color})`, height: '100%', width: `${pct}%`, borderRadius: 9999, transition: 'width 0.5s' }} />
    </div>
  );
}

function UserAvatar({ user, size = 32 }) {
  if (user.discord_avatar && user.discord_username) {
    const did = user.discord_id || '';
    return <img src={`https://cdn.discordapp.com/avatars/${did}/${user.discord_avatar}.png`} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />;
  }
  const i = ((user.discord_username || '?')[0] || '?').toUpperCase();
  return <div style={{ width: size, height: size, borderRadius: '50%', background: '#2d1b69', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a78bfa', fontWeight: 700, fontSize: size * 0.4 }}>{i}</div>;
}

// ───────── Drawer Dettaglio Utente ─────────
function UserDrawer({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('attivita');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/admin/analytics/utenti/${userId}/dettaglio`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [userId]);

  if (!userId) return null;

  const tabs = [
    { key: 'attivita', label: '📊 Attivita' },
    { key: 'prodotti', label: '📦 Prodotti' },
    { key: 'corso', label: '🎓 Corso' },
    { key: 'abbonamento', label: '💳 Abbonamento' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 520, height: '100%', background: '#0d0b1e', borderLeft: '1px solid #2d1b69', overflowY: 'auto', animation: 'slideInRight 0.3s ease-out' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#9ca3af', fontSize: '1.3rem', cursor: 'pointer', zIndex: 10 }}>✕</button>

        {loading || !data ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Caricamento...</div>
        ) : (
          <>
            {/* Header */}
            <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #2d1b69' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <UserAvatar user={{ discord_avatar: data.profilo.discord_avatar, discord_username: data.profilo.discord_username, discord_id: data.profilo.discord_id }} size={52} />
                <div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e2e8f0', fontFamily: 'Syne,sans-serif' }}>{data.profilo.discord_username || data.profilo.name}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <PianoBadge piano={data.abbonamento.piano} />
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Dal {data.profilo.created_at}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #2d1b69', padding: '0 16px' }}>
              {tabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '10px 12px', fontSize: '0.78rem', fontWeight: 600, color: tab === t.key ? '#a78bfa' : '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', borderBottom: tab === t.key ? '2px solid #a78bfa' : '2px solid transparent', whiteSpace: 'nowrap' }}>{t.label}</button>
              ))}
            </div>

            <div style={{ padding: 20 }}>
              {/* Tab Attivita */}
              {tab === 'attivita' && (
                <>
                  <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 12 }}>Attivita ultime 8 settimane</p>
                  <div style={{ height: 200, marginBottom: 24 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.attivita_per_settimana}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2d1b69" />
                        <XAxis dataKey="settimana" tick={{ fill: '#9ca3af', fontSize: 10 }} tickFormatter={v => v.split('-W')[1] ? `W${v.split('-W')[1]}` : v} />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 8, fontSize: '0.78rem' }} />
                        <Bar dataKey="prodotti" fill="#a78bfa" radius={[4,4,0,0]} />
                        <Bar dataKey="vendite" fill="#4ade80" radius={[4,4,0,0]} />
                        <Bar dataKey="annunci" fill="#fbbf24" radius={[4,4,0,0]} />
                        <Bar dataKey="analisi" fill="#22d3ee" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 12 }}>Distribuzione prodotti</p>
                  <div style={{ height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={[
                          { name: 'Disponibile', value: data.distribuzione_prodotti.disponibile || 0 },
                          { name: 'Venduto', value: data.distribuzione_prodotti.venduto || 0 },
                          { name: 'Riservato', value: data.distribuzione_prodotti.riservato || 0 },
                        ].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={4} dataKey="value">
                          <Cell fill="#4ade80" /><Cell fill="#a78bfa" /><Cell fill="#fbbf24" />
                        </Pie>
                        <Tooltip contentStyle={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}

              {/* Tab Prodotti */}
              {tab === 'prodotti' && (
                <>
                  <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 12 }}>Ultimi prodotti</p>
                  {(data.prodotti_recenti || []).length === 0 ? <p style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Nessun prodotto</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                      {data.prodotti_recenti.map((p, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#1a1035', borderRadius: 10, border: '1px solid #2d1b69' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0' }}>{p.nome}</div>
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{p.data}</div>
                          </div>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e2e8f0' }}>{'\u20AC'}{(p.prezzo || 0).toFixed(2)}</span>
                          <span style={{ fontSize: '0.65rem', padding: '2px 6px', borderRadius: 6, background: p.stato === 'venduto' ? 'rgba(167,139,250,0.15)' : 'rgba(74,222,128,0.15)', color: p.stato === 'venduto' ? '#a78bfa' : '#4ade80' }}>{p.stato}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 12 }}>Ultime vendite</p>
                  {(data.vendite_recenti || []).length === 0 ? <p style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Nessuna vendita</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {data.vendite_recenti.map((v, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#1a1035', borderRadius: 10, border: '1px solid #2d1b69' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0' }}>{v.prodotto}</div>
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{v.data} · {v.piattaforma}</div>
                          </div>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#4ade80' }}>{'\u20AC'}{(v.prezzo || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Tab Corso */}
              {tab === 'corso' && (
                <>
                  <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 16 }}>Progresso per modulo</p>
                  {Object.entries(data.corso_dettaglio || {}).map(([nome, val]) => (
                    <div key={nome} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.78rem', color: '#e2e8f0' }}>{nome}</span>
                        <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{val.completato}/{val.totale}</span>
                      </div>
                      <ProgressBar value={val.completato} max={val.totale || 1} color="#a78bfa" />
                    </div>
                  ))}
                </>
              )}

              {/* Tab Abbonamento */}
              {tab === 'abbonamento' && (
                <>
                  <div style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Piano:</span>
                      <PianoBadge piano={data.abbonamento.piano} />
                    </div>
                    {data.abbonamento.data_upgrade && <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Upgrade: {data.abbonamento.data_upgrade}</p>}
                    {data.abbonamento.stripe_customer_id && <p style={{ fontSize: '0.72rem', color: '#4b5563', marginTop: 4 }}>Stripe: {data.abbonamento.stripe_customer_id}</p>}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ───────── Sezione Costi API ─────────
function CostiAPISection() {
  const [costi, setCosti] = useState(null);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/costi/overview').then(r => setCosti(r.data)),
      api.get('/admin/costi/per-utente').then(r => setUtenti(r.data))
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Caricamento costi...</div>;
  if (!costi) return null;

  const usdToEur = (v) => (v * 0.92).toFixed(2);
  const chartData = (costi.grafico_giornaliero || []).map(d => ({
    ...d,
    label: d.data ? `${d.data.slice(8,10)}/${d.data.slice(5,7)}` : '',
    claude: +(d.claude * 0.92).toFixed(4),
    serpapi: +(d.serpapi * 0.92).toFixed(4)
  }));

  return (
    <div style={{ marginBottom: 32 }}>
      {/* Alert soglie */}
      {costi.costo_totale_oggi * 0.92 > 5 && (
        <div style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid #f87171', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '1.1rem' }}>⚠️</span>
          <span style={{ color: '#f87171', fontWeight: 700, fontSize: '0.85rem' }}>Costi elevati oggi: €{usdToEur(costi.costo_totale_oggi)}</span>
        </div>
      )}
      {costi.proiezione_mensile * 0.92 > 50 && (
        <div style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid #fbbf24', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '1.1rem' }}>⚠️</span>
          <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: '0.85rem' }}>Proiezione mensile elevata: €{usdToEur(costi.proiezione_mensile)}</span>
        </div>
      )}

      {/* KPI Cards */}
      <p style={{ fontSize: '0.95rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 14, fontFamily: 'Syne,sans-serif' }}>💰 Costi API</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiCard icon="💰" label="Costo oggi" value={`€${usdToEur(costi.costo_totale_oggi)}`} sub={`${costi.chiamate_oggi} chiamate`} color="#f87171" />
        <KpiCard icon="📅" label="Costo mese" value={`€${usdToEur(costi.costo_totale_mese)}`} sub={`${costi.chiamate_mese} chiamate totali`} color="#fbbf24" />
        <KpiCard icon="📈" label="Proiezione mese" value={`€${usdToEur(costi.proiezione_mensile)}`} sub="basata su ultimi 7gg" color="#a78bfa" />
        <KpiCard icon="👤" label="Costo medio PRO" value={`€${usdToEur(costi.costo_medio_per_utente_pro)}`} sub={`Top: ${costi.utente_piu_costoso.username}`} color="#22d3ee" />
      </div>

      {/* Grafico costi */}
      {chartData.length > 0 && (
        <div style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 14, padding: '20px 16px', marginBottom: 20 }}>
          <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 16 }}>Costi ultimi 30 giorni</p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2d1b69" />
                <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 10 }} interval={4} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} tickFormatter={v => `€${v}`} />
                <Tooltip contentStyle={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 8, fontSize: '0.78rem' }} formatter={(v) => `€${v.toFixed(4)}`} />
                <Bar dataKey="claude" stackId="a" fill="#a78bfa" radius={[0,0,0,0]} name="Claude AI" />
                <Bar dataKey="serpapi" stackId="a" fill="#fbbf24" radius={[4,4,0,0]} name="SerpAPI" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Breakdown per tipo */}
      {Object.keys(costi.costo_per_tipo || {}).length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {Object.entries(costi.costo_per_tipo).map(([tipo, costo]) => (
            <div key={tipo} style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 10, padding: '10px 14px', minWidth: 130 }}>
              <div style={{ fontSize: '0.68rem', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>{tipo.replace('claude_', 'Claude ').replace('serpapi', 'SerpAPI')}</div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: tipo.includes('claude') ? '#a78bfa' : '#fbbf24' }}>€{usdToEur(costo)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabella costi per utente */}
      <div style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #2d1b69', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e2e8f0' }}>Costi per utente</span>
          <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{utenti.length} utenti con consumi</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2d1b69' }}>
                {['Utente', 'Piano', 'Costo mese', 'Claude', 'SerpAPI', 'Token totali'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#9ca3af', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {utenti.map(u => (
                <tr key={u.user_id} style={{ borderBottom: '1px solid rgba(45,27,105,0.4)' }}>
                  <td style={{ padding: '8px 12px', color: '#e2e8f0', fontWeight: 600 }}>{u.username}</td>
                  <td style={{ padding: '8px 12px' }}><PianoBadge piano={u.piano} /></td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ color: u.costo_questo_mese * 0.92 > 2 ? '#f87171' : '#e2e8f0', fontWeight: 700 }}>
                      €{(u.costo_questo_mese * 0.92).toFixed(2)}
                    </span>
                    {u.costo_questo_mese * 0.92 > 2 && <span style={{ marginLeft: 4, fontSize: '0.65rem', background: 'rgba(248,113,113,0.15)', color: '#f87171', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>ALTO</span>}
                  </td>
                  <td style={{ padding: '8px 12px', color: '#a78bfa' }}>{u.chiamate_claude}</td>
                  <td style={{ padding: '8px 12px', color: '#fbbf24' }}>{u.chiamate_serpapi}</td>
                  <td style={{ padding: '8px 12px', color: '#9ca3af', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}>{(u.tokens_totali || 0).toLocaleString()}</td>
                </tr>
              ))}
              {utenti.length === 0 && <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>Nessun consumo API registrato</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ───────── Componente Principale ─────────
export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [utenti, setUtenti] = useState([]);
  const [totale, setTotale] = useState(0);
  const [pagine, setPagine] = useState(1);
  const [page, setPage] = useState(1);
  const [filtro, setFiltro] = useState('tutti');
  const [ordine, setOrdine] = useState('data_iscrizione');
  const [ricerca, setRicerca] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    api.get('/admin/analytics/overview').then(r => setOverview(r.data)).catch(() => {});
  }, []);

  const loadUtenti = useCallback(() => {
    setLoading(true);
    api.get('/admin/analytics/utenti', { params: { page, limit: 20, filtro, ordine, ricerca } })
      .then(r => { setUtenti(r.data.utenti || []); setTotale(r.data.totale || 0); setPagine(r.data.pagine || 1); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [page, filtro, ordine, ricerca]);

  useEffect(() => { loadUtenti(); }, [loadUtenti]);

  const handleExport = () => {
    api.get('/admin/analytics/export-csv', { responseType: 'blob' }).then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'analytics_utenti.csv'; a.click();
    }).catch(() => {});
  };

  const ov = overview || {};
  const convRate = ov.totale_utenti > 0 ? ((ov.utenti_pro / ov.totale_utenti) * 100).toFixed(1) : '0';
  const chartData = (ov.crescita_utenti || []).map(d => ({ ...d, label: d.data ? `${d.data.slice(8, 10)}/${d.data.slice(5, 7)}` : '' }));

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <KpiCard icon="👥" label="Totale Utenti" value={ov.totale_utenti || 0} sub={`+${ov.nuovi_utenti_7gg || 0} questa settimana`} />
        <KpiCard icon="💎" label="Utenti PRO" value={ov.utenti_pro || 0} sub={`Revenue: €${(ov.revenue_stimata_mensile || 0).toFixed(0)}/mese`} color="#a78bfa" />
        <KpiCard icon="📦" label="Prodotti" value={ov.totale_prodotti_piattaforma || 0} sub={`${ov.totale_vendite_piattaforma || 0} vendite`} color="#22d3ee" />
        <KpiCard icon="💰" label="Valore Venduto" value={`€${(ov.totale_valore_venduto || 0).toFixed(0)}`} sub={`${ov.totale_analisi_mercato || 0} analisi AI`} color="#4ade80" />
        <KpiCard icon="📈" label="Conversione" value={`${convRate}%`} sub="FREE → PRO" color="#fbbf24" />
        <KpiCard icon="🤖" label="Annunci AI" value={ov.totale_annunci_generati || 0} sub="generati totali" color="#22d3ee" />
      </div>

      {/* Sezione Costi API */}
      <CostiAPISection />

      {/* Grafico crescita */}
      {chartData.length > 0 && (
        <div style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 14, padding: '20px 16px', marginBottom: 24 }}>
          <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 16, fontFamily: 'Syne,sans-serif' }}>📈 Crescita utenti — ultimi 30 giorni</p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a78bfa" stopOpacity={0.3} /><stop offset="100%" stopColor="#a78bfa" stopOpacity={0} /></linearGradient>
                  <linearGradient id="grad2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4ade80" stopOpacity={0.3} /><stop offset="100%" stopColor="#4ade80" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2d1b69" />
                <XAxis dataKey="label" tick={{ fill: '#9ca3af', fontSize: 10 }} interval={4} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 8, fontSize: '0.78rem' }} />
                <Area type="monotone" dataKey="totale" stroke="#a78bfa" fill="url(#grad1)" strokeWidth={2} name="Totale" />
                <Area type="monotone" dataKey="nuovi" stroke="#4ade80" fill="url(#grad2)" strokeWidth={2} name="Nuovi" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filtri + Export */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select value={filtro} onChange={e => { setFiltro(e.target.value); setPage(1); }} className="input" style={{ width: 'auto', minWidth: 140, fontSize: '0.82rem' }}>
          <option value="tutti">Tutti</option>
          <option value="pro">Solo PRO</option>
          <option value="free">Solo FREE</option>
          <option value="churn">A rischio churn</option>
          <option value="upgrade">Potenziale upgrade</option>
        </select>
        <select value={ordine} onChange={e => { setOrdine(e.target.value); setPage(1); }} className="input" style={{ width: 'auto', minWidth: 160, fontSize: '0.82rem' }}>
          <option value="data_iscrizione">Data iscrizione</option>
          <option value="score">Score attivita</option>
          <option value="prodotti">Piu prodotti</option>
          <option value="vendite">Piu vendite</option>
        </select>
        <input value={ricerca} onChange={e => { setRicerca(e.target.value); setPage(1); }} className="input" placeholder="Cerca username..." style={{ width: 'auto', minWidth: 180, fontSize: '0.82rem' }} />
        <div style={{ flex: 1 }} />
        <button onClick={handleExport} style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', border: 'none', borderRadius: 9999, padding: '8px 18px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>📥 Export CSV</button>
      </div>

      {/* Tabella utenti */}
      <div style={{ background: '#1a1035', border: '1px solid #2d1b69', borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2d1b69' }}>
                {['Utente', 'Piano', 'Iscrizione', 'Score', 'Prodotti', 'Vendite', 'Valore €', 'Corso', 'Churn', ''].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#9ca3af', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Caricamento...</td></tr>
              ) : utenti.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Nessun utente trovato</td></tr>
              ) : utenti.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid rgba(45,27,105,0.4)' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#1a1035'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <UserAvatar user={u} size={28} />
                      <span style={{ color: '#e2e8f0', fontWeight: 600, whiteSpace: 'nowrap' }}>{u.discord_username}</span>
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px' }}><PianoBadge piano={u.piano} /></td>
                  <td style={{ padding: '8px 12px' }}><AccessoBadge data={u.data_iscrizione} /></td>
                  <td style={{ padding: '8px 12px', minWidth: 100 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ProgressBar value={u.score_attivita} color={u.score_attivita >= 50 ? '#4ade80' : u.score_attivita >= 20 ? '#fbbf24' : '#f87171'} />
                      <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.72rem', minWidth: 20 }}>{u.score_attivita}</span>
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{u.prodotti_totali}</td>
                  <td style={{ padding: '8px 12px', color: '#4ade80' }}>{u.vendite_totali}</td>
                  <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{'\u20AC'}{(u.valore_venduto || 0).toFixed(0)}</td>
                  <td style={{ padding: '8px 12px', minWidth: 60 }}>
                    <ProgressBar value={u.corso_completamento} color="#a78bfa" />
                  </td>
                  <td style={{ padding: '8px 12px' }}><ChurnBadge rischio={u.rischio_churn} /></td>
                  <td style={{ padding: '8px 12px' }}>
                    <button onClick={() => setSelectedUser(u.id)} style={{ background: '#2d1b69', border: 'none', color: '#a78bfa', borderRadius: 8, padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>👁 Dettaglio</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginazione */}
      {pagine > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
          {Array.from({ length: pagine }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} style={{ width: 32, height: 32, borderRadius: 8, border: page === p ? '1px solid #7c3aed' : '1px solid #2d1b69', background: page === p ? 'rgba(124,58,237,0.2)' : 'transparent', color: page === p ? '#a78bfa' : '#9ca3af', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>{p}</button>
          ))}
        </div>
      )}

      {/* Drawer dettaglio */}
      <UserDrawer userId={selectedUser} onClose={() => setSelectedUser(null)} />
    </div>
  );
}
